import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuSeparator,
    DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { 
    BookOpen, 
    Video, 
    GraduationCap, 
    User, 
    LogOut, 
    Menu,
    Moon,
    Sun,
    Home,
    Plane,
    Users,
    Link as LinkIcon,
    Phone,
    Star,
    Sparkles,
    Mail,
    LayoutDashboard
} from 'lucide-react';

// Admin email check
const ADMIN_EMAILS = ["flywithshelly@gmail.com"];

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [darkMode, setDarkMode] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
        document.documentElement.classList.toggle('dark');
    };

    const isAdmin = user?.email?.toLowerCase() && ADMIN_EMAILS.includes(user.email.toLowerCase());

    const navLinks = [
        { href: '/', label: 'Home', icon: Home },
        { href: '/book', label: 'Book', icon: BookOpen },
        { href: '/course', label: 'Courses', icon: GraduationCap },
        { href: '/membership', label: 'Membership', icon: Users },
        { href: '/community', label: 'Community', icon: Video },
        { href: '/coaching', label: 'Coaching\n+ Resume', icon: Phone, multiline: true },
        { href: '/mentorship', label: 'Mentorship', icon: Sparkles },
        { href: '/reviews', label: 'Reviews', icon: Star },
        { href: '/airlines', label: 'Airlines Hiring', icon: LinkIcon },
        { href: '/contact', label: 'Contact', icon: Mail },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                        <Plane className="h-6 w-6 text-primary" />
                        <span className="font-heading text-xl font-semibold hidden sm:inline">
                            Flight Attendant Academy
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                data-testid={`nav-${link.label.toLowerCase().replace(/[\s\n+]/g, '-')}`}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isActive(link.href)
                                        ? 'bg-primary text-primary-foreground'
                                        : 'hover:bg-secondary'
                                } ${link.multiline ? 'text-center leading-tight' : ''}`}
                            >
                                {link.multiline ? (
                                    <span className="whitespace-pre-line">{link.label}</span>
                                ) : (
                                    link.label
                                )}
                            </Link>
                        ))}
                    </nav>

                    {/* Right side */}
                    <div className="flex items-center gap-2">
                        {/* Theme toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleDarkMode}
                            data-testid="theme-toggle"
                            className="rounded-full"
                        >
                            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>

                        {/* User menu or auth buttons */}
                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="rounded-full gap-2"
                                        data-testid="user-menu-trigger"
                                    >
                                        <User className="h-5 w-5" />
                                        <span className="hidden sm:inline">{user.name}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem 
                                        onClick={() => navigate('/dashboard')}
                                        data-testid="menu-dashboard"
                                    >
                                        <User className="mr-2 h-4 w-4" />
                                        Dashboard
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                        onClick={() => navigate('/live-classes')}
                                    >
                                        <Video className="mr-2 h-4 w-4" />
                                        Live Classes
                                    </DropdownMenuItem>
                                    {isAdmin && (
                                        <DropdownMenuItem 
                                            onClick={() => navigate('/admin')}
                                            data-testid="menu-admin"
                                        >
                                            <LayoutDashboard className="mr-2 h-4 w-4" />
                                            Admin Dashboard
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={logout}
                                        data-testid="menu-logout"
                                    >
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="hidden sm:flex gap-2">
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate('/login')}
                                    data-testid="nav-login"
                                    className="rounded-full"
                                >
                                    Login
                                </Button>
                                <Button
                                    onClick={() => navigate('/register')}
                                    data-testid="nav-register"
                                    className="rounded-full"
                                >
                                    Get Started
                                </Button>
                            </div>
                        )}

                        {/* Mobile menu */}
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild className="lg:hidden">
                                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-72">
                                <nav className="flex flex-col gap-2 mt-8">
                                    {navLinks.map((link) => {
                                        const Icon = link.icon;
                                        return (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                onClick={() => setMobileOpen(false)}
                                                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[\s\n+]/g, '-')}`}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                                                    isActive(link.href)
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'hover:bg-secondary'
                                                }`}
                                            >
                                                <Icon className="h-5 w-5" />
                                                {link.multiline ? link.label.replace('\n', ' ') : link.label}
                                            </Link>
                                        );
                                    })}
                                    
                                    {user && (
                                        <Link
                                            to="/live-classes"
                                            onClick={() => setMobileOpen(false)}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium hover:bg-secondary"
                                        >
                                            <Video className="h-5 w-5" />
                                            Live Classes
                                        </Link>
                                    )}
                                    
                                    {!user && (
                                        <>
                                            <hr className="my-4" />
                                            <Link
                                                to="/login"
                                                onClick={() => setMobileOpen(false)}
                                                className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium hover:bg-secondary"
                                            >
                                                Login
                                            </Link>
                                            <Link
                                                to="/register"
                                                onClick={() => setMobileOpen(false)}
                                                className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium bg-primary text-primary-foreground"
                                            >
                                                Get Started
                                            </Link>
                                        </>
                                    )}
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>

            {/* Main content */}
            <main>{children}</main>

            {/* Footer */}
            <footer className="border-t py-8 mt-16">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Plane className="h-5 w-5 text-primary" />
                            <span className="font-heading font-medium">Flight Attendant Academy by Shelly</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            &copy; {new Date().getFullYear()} All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Layout;
